import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable, tap } from 'rxjs';

import { selectorObj } from '../../interfaces/ISelector';
import { ISubmitBody } from '../../interfaces/ISubmitBody';
import { EndpointService } from '../endpoint/endpoint.service';

import {
  APP_ID,
  DELIVERY_LIST_TYPE,
  DISPENSE_ACTIONS,
  NETWORK_ENDPOINT,
  SCAN_TYPE,
} from './../../utils/constants';

export abstract class HelmsdeepServiceAPI {
  gandalfUrl: string;
  helmsdeepUrl: string;
  headers = { 'content-type': 'application/json' };

  constructor(public http: HttpClient, public endpointService: EndpointService) {
    this.gandalfUrl = `${this.endpointService.getGandalfUrl()}/api/submit`;
    this.helmsdeepUrl = this.endpointService.getHelmsdeepUrl();
  }

  createOrder(order: any): Observable<any> {
    const body: ISubmitBody = {
      appId: APP_ID,
      method: 'POST',
      url: 'schedules',
      payload: order,
    };
    return this.http.post(this.gandalfUrl, body, { headers: this.headers });
  }

  unloadOrder(
    scheduleId: string,
    side?: string,
    screenType?: string,
    dispenseId?: string
  ): Observable<object> {
    const integratedTypes = [SCAN_TYPE, DELIVERY_LIST_TYPE];
    const isCoreType = !integratedTypes.includes(screenType);
    const actionType = isCoreType ? DISPENSE_ACTIONS.DO_CANCELLED : DISPENSE_ACTIONS.DO_UNDO;
    const scheduleToload: ISubmitBody = {
      appId: APP_ID,
      method: 'POST',
      url: `schedules/${scheduleId}/_actions`,
      payload: [
        {
          type: 'DO_TRANSACTION',
          params: {
            dispenses: dispenseId ? [dispenseId] : undefined,
            actionType: actionType,
            meta: {
              ...(side && { side }),
            },
          },
        },
      ],
    };
    return this.http.post(this.gandalfUrl, scheduleToload, { headers: this.headers }).pipe(
      tap(() => {
        if (!dispenseId && actionType === 'DO_UNDO') {
          this.cleanUpSchedule(scheduleId);
        }
      })
    );
  }

  deleteOrder(scheduleId: string): Observable<any> {
    const body = {
      appId: APP_ID,
      method: 'DELETE',
      url: `schedules/${scheduleId}`,
    };
    return this.http
      .post(this.gandalfUrl, body, { headers: this.headers })
      .pipe(tap((order) => console.log('delete order: ', JSON.stringify(order))));
  }

  abstract finishLoadOrder(scheduleId: string): Observable<object>;

  abstract addComparment(dispenseId: string): Observable<object>;

  getPreparers(): Observable<any> {
    const body = {
      appId: 'frodo',
      method: 'GET',
      url: 'preparers',
      payload: [],
    };
    return this.http.post(this.gandalfUrl, body, { headers: this.headers });
  }

  createUser(user: any): Observable<any> {
    const body = {
      appId: 'frodo',
      method: 'POST',
      url: 'users',
      payload: user.newUser,
    };
    return this.http.post(this.gandalfUrl, body, { headers: this.headers });
  }

  getEndUserBySelector(selector: selectorObj = {}): Observable<any> {
    const body = {
      appId: 'frodo',
      method: 'GET',
      url: 'users',
      payload: selector,
    };

    return this.http.post(this.gandalfUrl, body, { headers: this.headers });
  }

  deleteUser(id: string): Observable<any> {
    const body = {
      appId: 'frodo',
      method: 'DELETE',
      url: `users/${id}`,
    };
    return this.http.post(this.gandalfUrl, body, { headers: this.headers });
  }

  updateSettings(values: object): Observable<any> {
    const body = {
      appId: 'frodo',
      method: 'POST',
      url: 'settings',
      payload: {
        defaultSettings: values,
      },
    };
    return this.http.post(this.gandalfUrl, body, { headers: this.headers }).pipe(
      tap((response) => {
        console.log(
          'updateSettings response: ',
          JSON.stringify(response['defaultSettings']['vision10kFlow'])
        );
      })
    );
  }

  getData(endpoint: string, id?: string, include?: string, extraParams?: any): Observable<object> {
    let paramId = '';
    let extraParam = '';
    let paramInclude = '';
    if (id) {
      paramId = `/${id}`;
    }
    if (include) {
      paramInclude = `?include_docs=${include}`;
    }

    if (extraParams) {
      extraParam = `&${extraParams}`;
    }

    const envUrl = `${this.helmsdeepUrl}/${endpoint}${paramId}${paramInclude}${extraParam}`;
    return this.http.get(envUrl, { headers: this.headers });
  }

  postData(endpoint: string, body: any): Observable<object> {
    const envUrl = `${this.helmsdeepUrl}/${endpoint}`;
    return this.http.post(envUrl, body, { headers: this.headers });
  }

  getStatuses(endpoint: string, id?: string, statuses?: string[]): Observable<object> {
    const envUrl = `${this.helmsdeepUrl}/${endpoint}${id ? '/' + id : ''}${
      statuses ? '?statuses=' + statuses : ''
    }`;
    return this.http.get(envUrl, { headers: this.headers });
  }

  getNetworkConfig(): Observable<object> {
    return this.http.get(`${this.helmsdeepUrl}/${NETWORK_ENDPOINT}`);
  }

  setNetworkConfig(config: any): Observable<object> {
    return this.http.post(`${this.helmsdeepUrl}/${NETWORK_ENDPOINT}`, config, {
      headers: this.headers,
    });
  }

  deleteDispense(dispenseId: string): Observable<any> {
    const body = {
      appId: APP_ID,
      method: 'DELETE',
      url: `dispenses/${dispenseId}`,
    };
    return this.http
      .post(this.gandalfUrl, body, { headers: this.headers })
      .pipe(tap((order) => console.log('delete dispense: ', JSON.stringify(order))));
  }

  async cleanUpSchedule(scheduleId: string): Promise<any> {
    const response: any = await firstValueFrom(this.getData('schedules', scheduleId, 'dispenses'));
    if (response.dispenses && response.dispenses.length > 1) {
      const dispensesToRemove = response.dispenses.splice(1, response.dispenses.length);
      await Promise.all(
        dispensesToRemove.map((dispense) => firstValueFrom(this.deleteDispense(dispense.id)))
      );
    }
  }
}
